import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Calculator, TrendingUp, ClipboardCheck, ArrowLeft, CheckCircle2 } from "lucide-react";
import { BNTLogo, LoginVisionTagline } from "./layoutChrome";
import api, { formatApiError, API, tokenStore } from "@/lib/api";
import { clearDistributorReview } from "@/lib/distributorReview";
import {
  hasPlatformCenterAccess,
  resolvePostLoginPath,
} from "@/lib/postLoginRedirect";
import {
  applyWorkModuleForAccessContext,
  requiresAccessContextSelection,
} from "@/lib/workspaceAccessContext";
import { writePlatformDesignerBuyerPreview } from "@/lib/platformDesignerBuyerPreview";
import { PLATFORM_DESIGNER_BUYER_PREVIEW_EVENT } from "@/lib/usePlatformDesignerBuyerPreview";
import { VISOR_MODULE_DISPLAY_NAME } from "@/modules/workModuleCore";

const REMEMBER_KEY = "vf_remember_email";

/** Credenciales de prueba según respuesta del API (reales o sugeridas; fallback trial/123456). */
function trialCredentialsFromHint(h) {
  if (!h) return { username: "trial", password: "123456" };
  return {
    username: h.username ?? h.suggested_username ?? "trial",
    password: h.password ?? h.suggested_password ?? "123456",
  };
}

/** Tras login plataforma: destino Centro SaaS y sin vista previa comprador residual. */
function finishAuthenticatedSession(data, navigate) {
  if (hasPlatformCenterAccess(data)) {
    writePlatformDesignerBuyerPreview(false);
    window.dispatchEvent(
      new CustomEvent(PLATFORM_DESIGNER_BUYER_PREVIEW_EVENT, { detail: { active: false } }),
    );
  }
  if (!requiresAccessContextSelection(data)) {
    applyWorkModuleForAccessContext(data);
  }
  navigate(resolvePostLoginPath(data), { replace: true });
}


/** Mensaje claro según tipo de fallo en POST /auth/designer-login. */
function formatDesignerLoginError(err) {
  const status = err?.response?.status;
  if (!err?.response) {
    return "No hay conexión con el servidor. Compruebe que la aplicación está en ejecución (puerto 8000 o 8001).";
  }
  if (status === 404) {
    return "Esta instalación no tiene actualizado el acceso diseñador. Actualice el backend y la interfaz (git pull, yarn build:spa8000, reinicie el servicio).";
  }
  const detail = formatApiError(err.response?.data?.detail);
  if (detail) return detail;
  if (status === 401) {
    return "Credenciales de diseñador inválidas. Usuario Josebaldemar y contraseña 060771. Si cambió la clave, use «llave maestra» abajo.";
  }
  return `Error al entrar como diseñador (HTTP ${status}).`;
}


export default function Login() {
  const { login, register, error, setError, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot | designer
  const [designerSubmode, setDesignerSubmode] = useState("login"); // login | reset
  const [email, setEmail] = useState(localStorage.getItem(REMEMBER_KEY) || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1: email, 2: questions/answers
  const [securityData, setSecurityData] = useState(null); // { questions: [{ q }] }
  const [selectedQuestionIdx, setSelectedQuestionIdx] = useState("");
  const [securityAnswer, setSecurityAnswer] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [designerKey, setDesignerKey] = useState("");
  const [branding, setBranding] = useState({ logo_base64: "", nombre_comercial: "" });
  const [installationId, setInstallationId] = useState("");

  const [trialHint, setTrialHint] = useState(null);
  /** Maqueta del aviso trial sin depender del backend (solo desarrollo / flag explícito). */
  const [devTrialPreview, setDevTrialPreview] = useState(false);
  const [designerHint, setDesignerHint] = useState(null);
  const [designerHintError, setDesignerHintError] = useState("");
  const [platformHint, setPlatformHint] = useState(null);
  const [saasConfig, setSaasConfig] = useState(null);

  const loginDevShortcuts =
    process.env.NODE_ENV === "development" ||
    String(process.env.REACT_APP_LOGIN_DEV_SHORTCUTS || "").toLowerCase() === "true";

  useEffect(() => {
    api.get("/system/branding").then(({ data }) => setBranding(data)).catch(() => {});
    api.get("/system/installation-id").then(({ data }) => setInstallationId(data.installation_id)).catch(() => {});
    api.get("/system/trial-login-hint").then(({ data }) => setTrialHint(data)).catch(() => {});
    api.get("/system/platform-login-hint").then(({ data }) => setPlatformHint(data)).catch(() => setPlatformHint(null));
    api.get("/system/saas-config").then(({ data }) => setSaasConfig(data)).catch(() => setSaasConfig(null));
    api.get("/system/designer-login-hint")
      .then(({ data }) => {
        setDesignerHint(data);
        setDesignerHintError("");
      })
      .catch((err) => {
        setDesignerHint(null);
        const st = err?.response?.status;
        if (st === 404) {
          setDesignerHintError("Backend sin acceso diseñador (404). Actualice backend y SPA.");
        } else if (!err?.response) {
          setDesignerHintError("No se pudo contactar al servidor.");
        } else {
          setDesignerHintError(`Diagnóstico diseñador no disponible (HTTP ${st}).`);
        }
      });
  }, []);

  // Persistir el correo si el usuario marca "Recordar"
  useEffect(() => {
    if (remember && email) localStorage.setItem(REMEMBER_KEY, email);
    if (!remember) localStorage.removeItem(REMEMBER_KEY);
  }, [remember, email]);

  // Despertar el servidor en segundo plano apenas se carga el Login.
  // Usa GET /api/ (ligero, sin JWT) en lugar de /auth/me: evita colas largas y timeouts de 60s × 4.
  useEffect(() => {
    let cancelled = false;
    const wakeUp = async () => {
      for (let i = 0; i < 3; i++) {
        if (cancelled) return;
        try {
          await api.get("/", { timeout: 10000 });
          return;
        } catch {
          if (i < 2) await new Promise((r) => setTimeout(r, 1500));
        }
      }
    };
    wakeUp();
    return () => { cancelled = true; };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
    if (mode === "login") {
      const data = await login({ email, password, remember });
      if (data) {
        finishAuthenticatedSession(data, navigate);
      }
    } else if (mode === "register") {
      if (trialHint?.enabled) {
        await beginTrialOnboarding();
        return;
      }
      await register(name, email, password);
    } else if (mode === "designer") {
      if (designerSubmode === "login") {
        try {
          const { data } = await api.post(
            "/auth/designer-login",
            { username: (email || "").trim(), password: (password || "").trim(), remember },
            { timeout: 20000 },
          );
          if (data.access_token) {
            tokenStore.set(data.access_token, data.refresh_token, { remember: !!remember });
          }
          clearDistributorReview();
          if (!data?.is_platform_designer) {
            try {
              localStorage.setItem("visor:workspace_mode", "distribuidor");
            } catch { /* ignore */ }
          } else {
            try {
              localStorage.removeItem("visor:workspace_mode");
            } catch { /* ignore */ }
          }
          setUser(data);
          finishAuthenticatedSession(data, navigate);
        } catch (e) {
          setError(formatDesignerLoginError(e));
        }
      } else {
        try {
          await api.post("/auth/designer-reset", { email, designer_key: designerKey, new_password: newPassword });
          setForgotSent(true);
        } catch (e) {
          setError(formatApiError(e.response?.data?.detail) || "Llave de diseñador incorrecta");
        }
      }
    } else {
      // Olvidaste contraseña
      if (forgotStep === 1) {
        try {
          const normalizedEmail = (email || "").trim().toLowerCase();
          const { data } = await api.post("/auth/forgot-password", { email: normalizedEmail });
          if (data?.is_fake) {
            setError("No encontramos una cuenta registrada con ese correo. Verifique el email o regístrese.");
            return;
          }
          setSecurityData(data);
          setSelectedQuestionIdx("");
          setSecurityAnswer("");
          setConfirmNewPassword("");
          setForgotStep(2);
        } catch (e) {
          setError(formatApiError(e.response?.data?.detail) || "Error al buscar el correo");
        }
      } else if (
        securityData?.recovery_mode === "contact_admin" ||
        securityData?.recovery_mode === "owner_no_security"
      ) {
        return;
      } else {
        try {
          const qIdx = Number(selectedQuestionIdx);
          if (!Number.isInteger(qIdx) || qIdx < 0 || qIdx > 2) {
            setError("Elija una de las tres preguntas de seguridad");
            return;
          }
          if (!securityAnswer.trim()) {
            setError("Escriba la respuesta a la pregunta elegida");
            return;
          }
          if (newPassword !== confirmNewPassword) {
            setError("Las contraseñas nuevas no coinciden");
            return;
          }
          await api.post("/auth/security-reset", {
            email: (email || "").trim().toLowerCase(),
            question_index: qIdx,
            answer: securityAnswer,
            new_password: newPassword,
          });
          setForgotSent(true);
        } catch (e) {
          setError(formatApiError(e.response?.data?.detail) || "La respuesta no es correcta");
        }
      }
    }
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setDesignerSubmode("login");
    if (m === "designer" && designerHint?.username) {
      setEmail(designerHint.username);
    }
    setError("");
    setForgotSent(false);
    setForgotStep(1);
    setSecurityData(null);
    setSelectedQuestionIdx("");
    setSecurityAnswer("");
    setConfirmNewPassword("");
    setNewPassword("");
    setDesignerKey("");
    setPassword("");
  };

  let displayTrialHint = null;
  if (mode === "login") {
    if (trialHint?.enabled) {
      displayTrialHint = trialHint;
    } else if (devTrialPreview) {
      const { username: u, password: p } = trialCredentialsFromHint(trialHint);
      displayTrialHint = {
        enabled: true,
        preview_mode: true,
        username: u,
        password: p,
      };
    }
  }

  const trialCredsForHelp = trialCredentialsFromHint(trialHint);

  /** Instalación cliente / trial (incl. TRIAL_LOGIN_PREVIEW): registro inicial sin mostrar trial/123456. */
  const offersTrialOnboarding = mode === "login" && Boolean(trialHint?.enabled);

  const beginTrialOnboarding = async () => {
    setError("");
    setBusy(true);
    try {
      const { username, password } = trialCredentialsFromHint(trialHint);
      const { data } = await api.post(
        "/auth/login",
        { email: username, password, remember: false },
        { timeout: 20000 },
      );
      if (data.access_token) {
        tokenStore.set(data.access_token, data.refresh_token, { remember: !!remember });
      }
      if (!data.needs_trial_registration) {
        try {
          await api.post("/auth/logout");
        } catch {
          /* ignore */
        }
        tokenStore.clear();
        setUser(false);
        setError(
          "Esta instalación ya tiene un titular registrado. Use su correo y contraseña en «Entrar al sistema».",
        );
        return;
      }
        clearDistributorReview();
        setUser(data);
        navigate("/registro-inicial", { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(
        formatApiError(detail) ||
          "No se pudo iniciar el registro. Compruebe que el servidor esté activo o contacte a su distribuidor.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[56fr_44fr] bg-white">
      {/* Left brand panel */}
      <div className="hidden lg:flex relative bg-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute -top-16 -right-16 w-96 h-96 bg-[#0F2D5C] opacity-95 rotate-12 rounded-md"></div>
        <div className="absolute top-32 -right-8 w-64 h-44 bg-[#3DA873] opacity-90 -rotate-6 rounded-md"></div>
        <div className="absolute -bottom-20 -left-10 w-72 h-44 bg-[#3DA873]/15 -rotate-6 rounded-md"></div>
        <div className="absolute -bottom-32 -right-32 w-96 h-64 bg-[#0F2D5C]/10 rotate-6 rounded-md"></div>
        <div className="relative z-10">
          <BNTLogo
            size="lg"
            showDefault={!branding.logo_base64?.trim()}
            customLogo={branding.logo_base64}
            productTagline
          />
        </div>
        <div className="relative z-10 max-w-xl">
          <h1 className="text-5xl font-extrabold text-[#0F2D5C] leading-tight" style={{fontFamily:"'Chivo'"}}>
            {branding.nombre_comercial || "Visión y Logística Empresarial"}
          </h1>
          <p className="text-sm text-slate-600 mt-4 max-w-md">Confronta CFDI vs Excel · Reportes fiscales · {VISOR_MODULE_DISPLAY_NAME} con foco contable.</p>
          <div className="grid grid-cols-3 gap-6 mt-10">
            <Pillar icon={Calculator} title="Confronta" subtitle="Sistema vs Excel"/>
            <Pillar icon={TrendingUp} title="Reportes" subtitle="Tablero visual"/>
            <Pillar icon={ClipboardCheck} title="CFDI 4.0" subtitle="PUE / PPD"/>
          </div>
        </div>
        <div className="relative z-10 text-[10px] text-slate-400 uppercase tracking-widest">© BNT Sistemas · {VISOR_MODULE_DISPLAY_NAME} — Diseñado para contadores en México</div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-8 lg:p-9">
        <form onSubmit={onSubmit} className="w-full max-w-[422px] space-y-4" data-testid="login-form">
          <div className="lg:hidden mb-4">
            <BNTLogo
              size="md"
              showDefault={!branding.logo_base64?.trim()}
              customLogo={branding.logo_base64}
              productTagline
            />
          </div>

          <div className="border-l-4 border-[#3DA873] pl-3">
            <h2 className="text-2xl font-bold text-[#0F2D5C]" style={{fontFamily:"'Chivo'"}}>
              {mode === "login"
                ? `Acceso a ${VISOR_MODULE_DISPLAY_NAME}`
                : mode === "register"
                  ? "Crear cuenta"
                  : mode === "designer"
                    ? designerSubmode === "login"
                      ? "Acceso del diseñador"
                      : "Llave maestra del diseñador"
                    : "Recuperar acceso"}
            </h2>
            <div className="text-xs mt-1 space-y-1">
              {mode === "login" ? (
                <>
                  <LoginVisionTagline />
                  {offersTrialOnboarding ? (
                    <span className="block text-[10px] text-slate-500 normal-case tracking-normal">
                      Primera vez: registro de su empresa y periodo de prueba
                    </span>
                  ) : null}
                </>
              ) : mode === "register" ? (
                "Configura tu despacho en minutos"
              ) : mode === "designer" ? (
                designerSubmode === "login"
                  ? "Usuario y contraseña del emisor (acceso completo Despacho/Distribuidor)"
                  : "Llave maestra y nueva contraseña de otro usuario"
              ) : (
                "Recupera tu contraseña respondiendo tu pregunta"
              )}
            </div>
          </div>

          {mode === "register" && !forgotSent && (
            <p className="text-center text-xs text-slate-600 -mt-1" data-testid="register-back-to-login-top">
              ¿Ya tienes cuenta?{" "}
              <button
                type="button"
                data-testid="switch-to-login"
                onClick={() => switchMode("login")}
                className="text-[#0F2D5C] font-medium underline"
              >
                Inicia sesión
              </button>
            </p>
          )}

          {forgotSent ? (
            <div className="space-y-3">
              <div className="flex flex-col items-center text-center gap-2 py-6 border border-emerald-200 bg-emerald-50 rounded-sm px-4">
                <CheckCircle2 className="w-10 h-10 text-[#3DA873]"/>
                <p className="text-sm text-slate-700"><b>¡Éxito!</b> Tu contraseña ha sido actualizada correctamente.</p>
                <p className="text-xs text-slate-500">Ya puedes iniciar sesión con tu nueva clave.</p>
              </div>
              <Button type="button" variant="outline" onClick={()=>switchMode("login")}
                className="w-full rounded-sm h-10" data-testid="forgot-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2"/> Volver al inicio de sesión
              </Button>
            </div>
          ) : (
          <>
          {mode === "register" && (
            <div>
              <Label className="text-xs font-medium text-slate-700">Nombre completo</Label>
              <Input data-testid="register-name-input" value={name} onChange={(e)=>setName(e.target.value)} required
                className="mt-1 rounded-sm" placeholder="C.P. Juan Pérez" />
            </div>
          )}
          <div>
            <Label className="text-xs font-medium text-slate-700">
              {mode === "login"
                ? "Correo electrónico o usuario de acceso"
                : mode === "designer" && designerSubmode === "login"
                  ? "Usuario diseñador"
                  : "Correo electrónico"}
            </Label>
            <Input data-testid="auth-email-input" type={mode === "login" || (mode === "designer" && designerSubmode === "login") ? "text" : "email"} value={email}
              onChange={(e)=>setEmail(e.target.value)}
              required className="mt-1 rounded-sm font-mono" placeholder={
                mode === "login"
                  ? "correo@despacho.mx o usuario"
                  : mode === "designer" && designerSubmode === "login"
                    ? "Josebaldemar"
                    : "contacto@despacho.mx"
              }
              disabled={mode === "forgot" && forgotStep === 2} />
          </div>

          {mode === "login" && (
            <>
            <div>
              <Label className="text-xs font-medium text-slate-700">Contraseña</Label>
              <PasswordInput data-testid="auth-password-input" value={password}
                onChange={(e)=>setPassword(e.target.value.slice(0,128))}
                required minLength={4} maxLength={128}
                className="mt-1 rounded-sm font-mono" placeholder="••••••••" autoComplete="current-password" />
              <div className="text-right mt-1">
                <button type="button" onClick={()=>switchMode("forgot")}
                  className="text-[11px] text-[#0F2D5C] hover:text-[#3DA873] underline" data-testid="forgot-password-link">
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
              <p className="text-[10px] text-slate-500 mt-2 leading-snug">
                Sin respuesta a las preguntas de seguridad:{" "}
                <a href="/recuperacion-extravio" className="text-[#0F2D5C] font-medium underline">
                  Recuperación por extravío (PDF al distribuidor)
                </a>
              </p>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Checkbox id="remember" checked={remember} onCheckedChange={setRemember} data-testid="remember-checkbox"/>
              <Label htmlFor="remember" className="text-xs text-slate-600 cursor-pointer">
                Recordar este dispositivo (30 días)
              </Label>
            </div>

            {displayTrialHint?.enabled && !offersTrialOnboarding ? (
              <div
                className={`rounded-sm border px-3 py-3 space-y-2 text-left ${
                  displayTrialHint.preview_mode
                    ? "border-amber-300 bg-amber-50 text-amber-950"
                    : "border-sky-200 bg-sky-50 text-sky-950"
                }`}
              >
                {devTrialPreview && !trialHint?.enabled && trialHint?.bootstrap_ready === true ? (
                  <p className="text-[10px] text-emerald-950 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-2 leading-snug">
                    <strong>Usuario de prueba listo:</strong> la cuenta{" "}
                    <code className="font-mono">{trialCredsForHelp.username}</code> ya existe en la base de datos. Puede pulsar{" "}
                    <strong>Entrar al sistema</strong> y continuar con el registro si aplica.
                  </p>
                ) : null}
                {devTrialPreview && !trialHint?.enabled && trialHint?.bootstrap_ready !== true ? (
                  <p className="text-[10px] text-sky-950 bg-sky-50 border border-sky-200 rounded-sm px-2 py-2 leading-snug">
                    <strong>Nota (desarrollo):</strong> el aviso amarillo es una vista prevía. Las credenciales coinciden con las que
                    devuelve el API (<code className="font-mono">{trialCredsForHelp.username}</code> /{" "}
                    <code className="font-mono">{trialCredsForHelp.password}</code>). Si el inicio de sesión falla, en{" "}
                    <code className="font-mono">backend/.env</code> defina{" "}
                    <code className="font-mono">TRIAL_BOOTSTRAP_USERNAME={trialCredsForHelp.username}</code> y contraseña, o{" "}
                    <code className="font-mono">TRIAL_LOGIN_PREVIEW=true</code>, reinicie el API y vuelva a intentar.
                  </p>
                ) : null}
                <div className="flex flex-wrap items-center gap-2">
                  <p
                    className={`text-[11px] font-semibold ${
                      displayTrialHint.preview_mode ? "text-amber-950" : "text-sky-950"
                    }`}
                  >
                    Primera instalación — periodo de prueba
                  </p>
                  {displayTrialHint.preview_mode ? (
                    <span className="text-[9px] font-bold uppercase tracking-wide bg-amber-200/90 text-amber-950 px-2 py-0.5 rounded-sm">
                      Vista previa UI
                    </span>
                  ) : null}
                </div>
                {offersTrialOnboarding ? (
                  <>
                    <p
                      className={`text-[11px] leading-snug ${
                        displayTrialHint.preview_mode ? "text-amber-950" : "text-sky-950"
                      }`}
                    >
                      Pulse <strong>Comenzar registro inicial</strong> para dar de alta su empresa y definir{" "}
                      <strong>correo y contraseña</strong>. La licencia se gestiona después en{" "}
                      <strong>Configuración → Mi Negocio / Licencia</strong>.
                    </p>
                    {saasConfig?.public_registration ? (
                      <p className="text-[11px] text-slate-600 m-0">
                        ¿Prefiere alta SaaS?{" "}
                        <Link
                          to="/registro"
                          className="text-[#0F2D5C] font-medium underline"
                          data-testid="trial-hint-create-account-link"
                        >
                          Crear cuenta
                        </Link>
                      </p>
                    ) : null}
                  </>
                ) : (
                  <>
                    <p className="text-[11px] leading-snug">
                      Primera vez: entre con usuario{" "}
                      <code
                        className={`font-mono px-1 rounded text-[10px] ${
                          displayTrialHint.preview_mode ? "bg-white/80" : "bg-white/90"
                        }`}
                      >
                        {displayTrialHint.username}
                      </code>
                      {" · "}contraseña{" "}
                      <code
                        className={`font-mono px-1 rounded text-[10px] ${
                          displayTrialHint.preview_mode ? "bg-white/80" : "bg-white/90"
                        }`}
                      >
                        {displayTrialHint.password}
                      </code>
                    </p>
                    <p
                      className={`text-[10px] leading-relaxed ${
                        displayTrialHint.preview_mode ? "text-amber-950/95" : "text-sky-900/90"
                      }`}
                    >
                      En el primer acceso se pedirá el <strong>registro completo de su empresa</strong> y podrá elegir el{" "}
                      <strong>correo y la contraseña definitivos</strong> con los que usará el sistema durante los{" "}
                      <strong>días de prueba</strong>.
                    </p>
                  </>
                )}
                {displayTrialHint.preview_mode && !offersTrialOnboarding && loginDevShortcuts ? (
                  <p className="text-[10px] text-amber-900/90 leading-relaxed border-t border-amber-200/80 pt-2">
                    <strong>Maqueta sin cuenta en BD:</strong> use el botón «Vista trial» abajo o configure{" "}
                    <code className="font-mono">TRIAL_LOGIN_PREVIEW=true</code> en <code className="font-mono">backend/.env</code>{" "}
                    para el mismo flujo con usuario creado al arrancar.
                    {devTrialPreview ? (
                      <>
                        {" "}
                        Pulse «Quitar maqueta trial» para ocultar este aviso.
                      </>
                    ) : null}
                  </p>
                ) : null}
              </div>
            ) : null}
            </>
          )}

          {mode === "forgot" && forgotStep === 2 && securityData?.recovery_mode === "contact_admin" && (
            <div className="space-y-4 pt-2 border-t border-slate-100" data-testid="forgot-contact-admin-panel">
              <div className="rounded-sm border border-sky-200 bg-sky-50 px-4 py-4 text-left space-y-2">
                <p className="text-sm font-semibold text-[#0F2D5C]">Recuperación gestionada por su despacho</p>
                <p className="text-xs text-slate-700 leading-relaxed">
                  {securityData.message ||
                    "Las cuentas auxiliares no utilizan recuperación automática. Solicite a su administrador del despacho que restablezca su contraseña desde Configuración → Usuarios del equipo."}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => switchMode("login")}
                className="w-full rounded-sm h-10"
                data-testid="forgot-contact-admin-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Volver al inicio de sesión
              </Button>
            </div>
          )}

          {mode === "forgot" && forgotStep === 2 && securityData?.recovery_mode === "owner_no_security" && (
            <div className="space-y-4 pt-2 border-t border-slate-100" data-testid="forgot-owner-no-security-panel">
              <div className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-4 text-left space-y-2">
                <p className="text-sm font-semibold text-[#0F2D5C]">Preguntas de recuperación no configuradas</p>
                <p className="text-xs text-slate-700 leading-relaxed">
                  {securityData.message ||
                    "Configure sus preguntas en Configuración → Sistema o use la recuperación por extravío (PDF al distribuidor)."}
                </p>
                <p className="text-[11px] text-slate-600 leading-snug pt-1">
                  Sin respuesta a las preguntas de seguridad:{" "}
                  <a href="/recuperacion-extravio" className="text-[#0F2D5C] font-medium underline">
                    Recuperación por extravío (PDF al distribuidor)
                  </a>
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => switchMode("login")}
                className="w-full rounded-sm h-10"
                data-testid="forgot-owner-no-security-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Volver al inicio de sesión
              </Button>
            </div>
          )}

          {mode === "forgot" && forgotStep === 2 && securityData?.recovery_mode === "security_questions" && securityData?.questions && (
            <div className="space-y-6 pt-2 border-t border-slate-100">
              <p className="text-[11px] text-slate-600 leading-snug">
                Elija <strong>una</strong> de las preguntas que configuró en el sistema. Si la respuesta coincide, podrá
                definir su <strong>nueva contraseña de acceso</strong>.
              </p>

              <div className="space-y-2">
                <Label className="text-xs font-medium text-slate-700">Pregunta de seguridad</Label>
                <RadioGroup
                  value={selectedQuestionIdx}
                  onValueChange={setSelectedQuestionIdx}
                  className="space-y-2"
                >
                  {securityData.questions.map((item, qIdx) => (
                    <label
                      key={qIdx}
                      htmlFor={`sec-q-${qIdx}`}
                      className={`flex gap-3 rounded-sm border px-3 py-2.5 cursor-pointer transition-colors ${
                        selectedQuestionIdx === String(qIdx)
                          ? "border-[#3DA873] bg-emerald-50/50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <RadioGroupItem id={`sec-q-${qIdx}`} value={String(qIdx)} className="mt-0.5" />
                      <span className="text-sm text-[#0F2D5C] leading-snug">{item.q}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label className="text-[10px] text-slate-500">Su respuesta</Label>
                <Input
                  value={securityAnswer}
                  onChange={(e) => setSecurityAnswer(e.target.value)}
                  className="mt-1 rounded-sm text-sm"
                  placeholder="Respuesta a la pregunta elegida"
                  autoComplete="off"
                  disabled={selectedQuestionIdx === ""}
                />
              </div>

              <div className="space-y-3 pt-1">
                <div>
                  <Label className="text-xs font-medium text-slate-700">Nueva contraseña</Label>
                  <PasswordInput
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value.slice(0, 128))}
                    required
                    minLength={4}
                    maxLength={128}
                    className="mt-1 rounded-sm font-mono"
                    placeholder="Mínimo 4 caracteres"
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-700">Confirmar nueva contraseña</Label>
                  <PasswordInput
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value.slice(0, 128))}
                    required
                    minLength={4}
                    maxLength={128}
                    className="mt-1 rounded-sm font-mono"
                    placeholder="Repita la contraseña"
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <p className="text-[10px] text-slate-500 leading-snug border-t border-slate-100 pt-3">
                Si no recuerda la contraseña <strong>ni</strong> la respuesta, contacte a su{" "}
                <strong>distribuidor</strong>. Él puede restablecer el acceso desde el catálogo de compradores o, en una
                versión futura, mediante un <strong>PDF de código de acceso por extravío</strong> (mismo esquema que la
                activación de licencia).
              </p>
            </div>
          )}

          {mode === "register" && (
            <div>
              <Label className="text-xs font-medium text-slate-700">Contraseña</Label>
              <PasswordInput data-testid="auth-password-input" value={password} onChange={(e)=>setPassword(e.target.value)} required
                className="mt-1 rounded-sm font-mono" placeholder="••••••••" autoComplete="new-password" />
            </div>
          )}

          {mode === "designer" && designerSubmode === "login" && (
            <>
              
              {designerHintError ? (
                <p className="text-[10px] text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-2 py-2 leading-snug">
                  {designerHintError}
                </p>
              ) : null}
              {!designerHintError && designerHint && designerHint.auth_version !== 2 ? (
                <p className="text-[10px] text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-2 py-2 leading-snug">
                  Instalación con backend antiguo (auth_version {designerHint.auth_version ?? "?"}). Actualice para usar Josebaldemar / 060771.
                </p>
              ) : null}
              {!designerHintError && designerHint && designerHint.bootstrap_ready === false ? (
                <p className="text-[10px] text-sky-900 bg-sky-50 border border-sky-200 rounded-sm px-2 py-2 leading-snug">
                  La cuenta diseñador se creará automáticamente al entrar con la contraseña de fábrica <strong>060771</strong>.
                </p>
              ) : null}

              <div>
                <Label className="text-xs font-medium text-slate-700">Contraseña</Label>
                <PasswordInput
                  data-testid="designer-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value.slice(0, 128))}
                  required
                  minLength={4}
                  maxLength={128}
                  className="mt-1 rounded-sm font-mono"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Checkbox id="designer-remember" checked={remember} onCheckedChange={setRemember} />
                <Label htmlFor="designer-remember" className="text-xs text-slate-600 cursor-pointer">
                  Recordar este dispositivo (30 días)
                </Label>
              </div>
              <p className="text-[10px] text-slate-500 leading-snug border-t border-slate-100 pt-3">
                Acceso reservado al diseñador/emisor del software. Puede alternar libremente entre modo Despacho y
                Distribuidor.
              </p>
              <button
                type="button"
                onClick={() => {
                  setDesignerSubmode("reset");
                  setError("");
                  setPassword("");
                }}
                className="text-[10px] text-slate-400 hover:text-[#0F2D5C] underline"
              >
                ¿Perdiste acceso de un usuario? Usar llave maestra
              </button>
            </>
          )}

          {mode === "designer" && designerSubmode === "reset" && (
            <div className="space-y-4 pt-2 border-t border-slate-100">
              <p className="text-[11px] text-slate-500 italic">
                Usa esta opción si perdiste el acceso total. Solicita la llave maestra al diseñador.
              </p>
              <div>
                <Label className="text-xs font-medium text-slate-700">Llave Maestra del Diseñador</Label>
                <PasswordInput
                  value={designerKey}
                  onChange={(e)=>setDesignerKey(e.target.value)}
                  required
                  className="mt-1 rounded-sm font-mono"
                  placeholder="Llave proporcionada por el diseñador"
                  autoComplete="off"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Nueva contraseña (4-8 dígitos)</Label>
                <PasswordInput
                  value={newPassword}
                  onChange={(e)=>setNewPassword(e.target.value.slice(0,8))}
                  required
                  minLength={4}
                  maxLength={8}
                  className="mt-1 rounded-sm font-mono"
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setDesignerSubmode("login");
                  setError("");
                  setDesignerKey("");
                  setNewPassword("");
                }}
                className="text-[10px] text-slate-400 hover:text-[#0F2D5C] underline"
              >
                Volver al acceso diseñador
              </button>
            </div>
          )}

          {error && (
            <div data-testid="auth-error" className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
              {error}
            </div>
          )}


          {offersTrialOnboarding ? (
            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={busy}
              className="w-full bg-[#0F2D5C] hover:bg-[#0a1f42] text-white rounded-sm h-10 border-b-2 border-[#3DA873]"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Ingresar
            </Button>
          ) : null}

          {offersTrialOnboarding ? (
            <Button
              type="button"
              data-testid="begin-trial-onboarding-btn"
              disabled={busy}
              onClick={beginTrialOnboarding}
              variant="outline"
              className="w-full rounded-sm h-9 text-sm border-[#3DA873] text-[#0F2D5C] hover:bg-emerald-50/60"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Comenzar registro inicial
            </Button>
          ) : (
            !(mode === "forgot" && forgotStep === 2 && (
              securityData?.recovery_mode === "contact_admin" ||
              securityData?.recovery_mode === "owner_no_security"
            )) ? (
            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={busy}
              className="w-full bg-[#0F2D5C] hover:bg-[#0a1f42] text-white rounded-sm h-10 border-b-2 border-[#3DA873]"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {mode === "login"
                ? "Entrar al sistema"
                : mode === "register"
                  ? "Crear cuenta"
                  : mode === "designer"
                    ? designerSubmode === "login"
                      ? "Entrar como diseñador"
                      : "Resetear con Llave Maestra"
                    : forgotStep === 1
                      ? "Continuar"
                      : "Actualizar contraseña"}
            </Button>
            ) : null
          )}

          <div className="text-center text-xs text-slate-500 space-y-2">
            {mode === "login" ? (
              <>
                {offersTrialOnboarding && saasConfig?.public_registration ? (
                  <p className="text-[11px] text-slate-600 m-0">
                    ¿Prefiere alta SaaS?{" "}
                    <Link
                      to="/registro"
                      className="text-[#0F2D5C] font-medium underline"
                      data-testid="trial-hint-create-account-link"
                    >
                      Crear cuenta
                    </Link>
                  </p>
                ) : null}
                {!offersTrialOnboarding && displayTrialHint?.enabled ? (
                  <p className="text-[11px] text-slate-600 leading-snug max-w-sm mx-auto text-left">
                    ¿Ya completó el registro inicial? Entre con el <strong>correo</strong> o <strong>usuario</strong> y la
                    contraseña que definió.
                  </p>
                ) : null}
                {!offersTrialOnboarding && !displayTrialHint?.enabled && saasConfig?.public_registration ? (
                  <p>
                    ¿Sin cuenta?{" "}
                    <Link
                      to="/registro"
                      data-testid="switch-to-saas-register"
                      className="text-[#0F2D5C] font-medium underline"
                    >
                      Crear cuenta
                    </Link>
                  </p>
                ) : null}
                {!offersTrialOnboarding && !displayTrialHint?.enabled && !saasConfig?.public_registration ? (
                  <p>
                    ¿Sin cuenta?{" "}
                    <button
                      data-testid="switch-to-register"
                      type="button"
                      onClick={() => switchMode("register")}
                      className="text-[#0F2D5C] font-medium underline"
                    >
                      Regístrate
                    </button>
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={() => switchMode("designer")}
                  className="text-[10px] text-slate-400 hover:text-[#0F2D5C] underline"
                  data-testid="designer-access-link"
                >
                  Recuperación del Diseñador (Llave Maestra)
                </button>
              </>
            ) : mode !== "register" ? (
              <button data-testid="switch-to-login-from-forgot" type="button" onClick={()=>switchMode("login")} className="text-[#0F2D5C] font-medium underline inline-flex items-center gap-1">
                <ArrowLeft className="w-3 h-3"/> Volver al inicio de sesión
              </button>
            ) : null}
          </div>
          </>
          )}

          {loginDevShortcuts && !forgotSent && mode === "login" && (
            <div className="rounded-sm border border-dashed border-amber-500 bg-amber-50/95 px-3 py-2.5 space-y-2 text-left">
              <p className="text-[9px] font-bold text-amber-950 uppercase tracking-wider">
                Herramientas temporales de diseño
              </p>
              <p className="text-[10px] text-amber-900/95 leading-snug">
                Visibles con <code className="font-mono text-[9px]">yarn start</code> /{" "}
                <code className="font-mono text-[9px]">NODE_ENV=development</code> o con{" "}
                <code className="font-mono text-[9px]">REACT_APP_LOGIN_DEV_SHORTCUTS=true</code> en el build. Elimine el flag antes de
                empaquetar para clientes.
              </p>
              {(trialHint?.enabled || devTrialPreview) && (
                <p className="text-[10px] text-amber-950 leading-snug font-mono bg-white/70 border border-amber-200 rounded-sm px-2 py-1.5">
                  Acceso técnico bootstrap:{" "}
                  <strong>{trialCredsForHelp.username}</strong> / <strong>{trialCredsForHelp.password}</strong>
                  {trialHint?.preview_mode ? " · TRIAL_LOGIN_PREVIEW" : null}
                </p>
              )}
              {platformHint?.enabled ? (
                <p
                  className="text-[10px] text-amber-950 leading-snug font-mono bg-white/70 border border-amber-200 rounded-sm px-2 py-1.5"
                  data-testid="platform-login-hint-credentials"
                >
                  Acceso plataforma BNT:{" "}
                  <strong>{platformHint.email || platformHint.username}</strong> /{" "}
                  <strong>{platformHint.password}</strong>
                  {!platformHint.bootstrap_ready ? " · se creará al entrar" : null}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-[10px] border-amber-400 bg-white text-amber-950"
                  onClick={async () => {
                    setError("");
                    try {
                      const { data } = await api.get("/system/trial-login-hint");
                      setTrialHint(data);
                      setDevTrialPreview(true);
                      const { username: u, password: p } = trialCredentialsFromHint(data);
                      setEmail(u);
                      setPassword(p);
                    } catch {
                      setDevTrialPreview(true);
                      setEmail("trial");
                      setPassword("123456");
                    }
                  }}
                >
                  Vista trial (maqueta + credenciales)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-[10px] border-amber-400 bg-white text-amber-950"
                  onClick={() => setDevTrialPreview(false)}
                  disabled={!devTrialPreview}
                >
                  Quitar maqueta trial
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-[10px] border-amber-400 bg-white text-amber-950"
                  data-testid="platform-login-autofill"
                  disabled={!platformHint?.enabled}
                  onClick={() => {
                    if (!platformHint?.enabled) return;
                    setError("");
                    setDevTrialPreview(false);
                    switchMode("login");
                    setEmail(platformHint.email || platformHint.username || "");
                    setPassword(platformHint.password || "");
                  }}
                >
                  Autocompletar plataforma BNT
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-[10px] border-amber-400 bg-white text-amber-950"
                  onClick={() => {
                    setError("");
                    switchMode("designer");
                  }}
                >
                  Formulario diseñador
                </Button>
              </div>
            </div>
          )}

          {installationId && (
            <div className="text-[9px] text-slate-300 text-center font-mono">
              ID: {installationId}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

function Pillar({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex flex-col items-start">
      <Icon className="w-8 h-8 text-[#0F2D5C] mb-1" strokeWidth={1.5}/>
      <div className="text-[11px] font-bold uppercase tracking-wider text-[#0F2D5C]" style={{fontFamily:"'Chivo'"}}>{title}</div>
      <div className="text-[11px] font-bold uppercase tracking-wider text-[#3DA873]" style={{fontFamily:"'Chivo'"}}>{subtitle}</div>
    </div>
  );
}
